import { api } from './api';
import type {
  ActivityItem,
  BackgroundJob,
  BackgroundJobCloneBody,
  BackgroundJobCreate,
  BackgroundJobDashboard,
  BackgroundJobHeartbeat,
  BackgroundJobUpdate,
  BackgroundJobRun,
  BackgroundJobSchedule,
  BackgroundJobScheduleCreate,
  BackgroundJobScheduleUpdate,
  JobListParams,
  Paginated,
  RunListParams,
  StopRunResult,
} from '../types/background-job.types';

/**
 * Background Jobs — aligned to app/api/v1/routers/background_job_*_router.py
 * (prefix /api/v1/background-jobs).
 *
 * `/tick` is deliberately absent: it is an infrastructure endpoint guarded by a
 * service token and must never be called from the browser.
 */
const BASE = '/api/v1/background-jobs';

/** Every endpoint answers with the same envelope. */
interface Envelope<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
  /** the API sends `totalItems`; the aliases cover the other shapes in this codebase */
  pagination?: { totalItems?: number; total?: number; page?: number; pageSize?: number; page_size?: number; totalPages?: number; total_pages?: number };
}

function toPage<T>(payload: Envelope<T[] | { items?: T[] }>, fallbackSize: number): Paginated<T> {
  const raw = payload?.data as unknown;
  const items: T[] = Array.isArray(raw) ? raw as T[] : ((raw as { items?: T[] })?.items ?? []);
  const p = payload?.pagination ?? {};
  const pageSize = p.pageSize ?? p.page_size ?? fallbackSize;
  const total = p.totalItems ?? p.total ?? items.length;
  return {
    items,
    total,
    page: p.page ?? 1,
    pageSize,
    totalPages: p.totalPages ?? p.total_pages ?? Math.max(1, Math.ceil(total / (pageSize || 1))),
  };
}

/** What a FUNCTION job may name, and the execution types available. */
export interface JobFunctionKeys {
  functionKeys: string[];
  executionTypes: string[];
}

export const backgroundJobService = {
  /**
   * The registry the executor actually validates against.
   *
   * Fetched rather than copied into the client: a hardcoded list drifts the
   * moment someone registers a function without knowing a dropdown repeats it,
   * and nothing fails — the new function is simply unreachable from the only
   * screen that could configure it. Which is what had happened: six registered,
   * three offered.
   *
   * The appId is the project the JOB will belong to, not the caller's: the
   * executor resolves the key against that project's functions plus the shared
   * ones, so listing the caller's own instead offers keys the job cannot run.
   */
  listFunctionKeys: async (appId?: string | null): Promise<JobFunctionKeys> => {
    const res = await api.get<Envelope<JobFunctionKeys>>(`${BASE}/function-keys`,
      { params: appId ? { appId } : undefined });
    return res.data.data;
  },

  // ---- jobs ----
  listJobs: async (params: JobListParams = {}): Promise<Paginated<BackgroundJob>> => {
    const res = await api.get<Envelope<BackgroundJob[]>>(`${BASE}/list`, { params });
    return toPage<BackgroundJob>(res.data, params.page_size ?? 20);
  },

  getJob: async (jobId: string): Promise<BackgroundJob> => {
    const res = await api.get<Envelope<BackgroundJob>>(`${BASE}/${jobId}`);
    return res.data.data;
  },

  createJob: async (body: BackgroundJobCreate): Promise<BackgroundJob> => {
    const res = await api.post<Envelope<BackgroundJob>>(`${BASE}/create`, body);
    return res.data.data;
  },

  updateJob: async (jobId: string, body: BackgroundJobUpdate): Promise<BackgroundJob> => {
    const res = await api.put<Envelope<BackgroundJob>>(`${BASE}/${jobId}`, body);
    return res.data.data;
  },

  /** Soft-delete. 409 when schedules still reference the job. */
  deleteJob: async (jobId: string): Promise<void> => {
    await api.delete(`${BASE}/${jobId}`);
  },

  restoreJob: async (jobId: string): Promise<void> => {
    await api.patch(`${BASE}/${jobId}/restore`);
  },

  /** Runs through the same runner as the scheduler; takes no claim, so nextRunAt is unchanged. */
  runNow: async (jobId: string, parameters?: Record<string, unknown>): Promise<BackgroundJobRun> => {
    const res = await api.post<Envelope<BackgroundJobRun>>(`${BASE}/${jobId}/run`, { parameters: parameters ?? {} });
    return res.data.data;
  },

  // ---- schedules ----
  listSchedules: async (jobId: string): Promise<BackgroundJobSchedule[]> => {
    const res = await api.get<Envelope<BackgroundJobSchedule[]>>(`${BASE}/${jobId}/schedules`);
    const raw = res.data?.data as unknown;
    return Array.isArray(raw) ? raw as BackgroundJobSchedule[] : ((raw as { items?: BackgroundJobSchedule[] })?.items ?? []);
  },

  createSchedule: async (jobId: string, body: BackgroundJobScheduleCreate): Promise<BackgroundJobSchedule> => {
    const res = await api.post<Envelope<BackgroundJobSchedule>>(`${BASE}/${jobId}/schedules`, body);
    return res.data.data;
  },

  updateSchedule: async (scheduleId: string, body: BackgroundJobScheduleUpdate): Promise<BackgroundJobSchedule> => {
    const res = await api.put<Envelope<BackgroundJobSchedule>>(`${BASE}/schedules/${scheduleId}`, body);
    return res.data.data;
  },

  /** The correct "stop it now" action — reversible, history preserved. */
  toggleSchedule: async (scheduleId: string, isScheduleEnabled: boolean): Promise<BackgroundJobSchedule> => {
    const res = await api.patch<Envelope<BackgroundJobSchedule>>(`${BASE}/schedules/${scheduleId}/toggle`, { isScheduleEnabled });
    return res.data.data;
  },

  deleteSchedule: async (scheduleId: string): Promise<void> => {
    await api.delete(`${BASE}/schedules/${scheduleId}`);
  },

  /** Next N occurrences, for showing what a cron expression actually means. */
  previewSchedule: async (scheduleId: string, count = 5): Promise<{ occurrences: string[]; exhausted?: boolean }> => {
    const res = await api.get<Envelope<{ occurrences: string[]; exhausted?: boolean }>>(
      `${BASE}/schedules/${scheduleId}/preview`, { params: { count } });
    return res.data.data;
  },

  /**
   * Copy a job under a new key. The copy and any cloned schedules arrive
   * DISABLED — cloning must not silently double the work a deployment performs,
   * so the caller has to surface that rather than assume the copy is live.
   *
   * Gated on BACKGROUND_JOB.CREATE, not UPDATE: this produces a new job.
   */
  cloneJob: async (jobId: string, body: BackgroundJobCloneBody = {}): Promise<BackgroundJob> => {
    const res = await api.post<Envelope<BackgroundJob>>(`${BASE}/${jobId}/clone`, body);
    return res.data.data;
  },

  // ---- operations ----
  /** `appId` narrows to one project; omitted keeps every project merged. */
  getDashboard: async (appId?: string): Promise<BackgroundJobDashboard> => {
    const res = await api.get<Envelope<BackgroundJobDashboard>>(`${BASE}/dashboard`, {
      params: appId ? { appId } : undefined,
    });
    return res.data.data;
  },

  /**
   * Engine state for the process that answers this call (GET /heartbeat).
   *
   * Distinct from the dashboard: the tiles say what work EXISTS, this says
   * whether anything will actually run it. `pollerRunning` is the live task's
   * state, so a poller that died reads false while `engineEnabled` stays true.
   *
   * Never carries the tick token — only `globalTickConfigured`.
   */
  getHeartbeat: async (): Promise<BackgroundJobHeartbeat> => {
    const res = await api.get<Envelope<BackgroundJobHeartbeat>>(`${BASE}/heartbeat`);
    return res.data.data;
  },

  /** Audit events and run history, merged newest-first. `kind` filters to one. */
  listActivity: async (limit = 20, kind?: string, appId?: string): Promise<ActivityItem[]> => {
    const res = await api.get<Envelope<ActivityItem[]>>(`${BASE}/activity`, {
      params: { limit, ...(kind ? { kind } : {}), ...(appId ? { appId } : {}) },
    });
    const raw = res.data?.data as unknown;
    return Array.isArray(raw) ? raw as ActivityItem[] : [];
  },

  // ---- runs ----
  listRuns: async (params: RunListParams = {}): Promise<Paginated<BackgroundJobRun>> => {
    const res = await api.get<Envelope<BackgroundJobRun[]>>(`${BASE}/runs`, { params });
    return toPage<BackgroundJobRun>(res.data, params.page_size ?? 20);
  },

  getRun: async (runId: string): Promise<BackgroundJobRun> => {
    const res = await api.get<Envelope<BackgroundJobRun>>(`${BASE}/runs/${runId}`);
    return res.data.data;
  },

  /**
   * Ask a run to stop. Covers every attempt of that fire, so a retry queued behind
   * a failed attempt does not start either.
   *
   * Returns as soon as the request is RECORDED — it does not wait for the work to
   * end, and must not be read as "stopped". The run is executing on whichever
   * instance won its claim, which need not be the one answering this call, so the
   * caller polls the run's status like every other reader.
   *
   * `stopRequested: false` means there was nothing left to stop: the run finished
   * a moment before the button was pressed. That race is unavoidable and comes back
   * 200 with the run's terminal status rather than as an error.
   */
  stopRun: async (runId: string, reason?: string): Promise<StopRunResult> => {
    const res = await api.post<Envelope<StopRunResult>>(
      `${BASE}/runs/${runId}/stop`, null, { params: reason ? { reason } : undefined },
    );
    return res.data.data;
  },
};

export default backgroundJobService;
